<template>
  <div class="dark:bg-slate-900 h-full payment-dashboard-ui">
    <div class="flex justify-space-between">
      <div class="agent-dropdown z-[3]">
        <multiselect
          v-model="assignedAgent"
          track-by="id"
          label="name"
          :placeholder="
            $t('AGENT_MGMT.MULTI_SELECTOR.SEARCH.PLACEHOLDER.AGENT')
          "
          selected-label
          :select-label="$t('CONTACT_FORM.FORM.STATUS.SELECT_PLACEHOLDER')"
          :deselect-label="$t('CONTACT_FORM.FORM.STATUS.REMOVE')"
          :custom-label="countryNameWithCode"
          :max-height="160"
          :options="agentList"
          :allow-empty="true"
          :option-height="104"
          @input="onClickAssignAgent"
        />
      </div>
      <div class="relative">
        <div class="filter-date flex gap-3">
          <div
            class="bg-white dark:bg-slate-900 px-3 cursor-pointer filter-color"
            :class="{ 'import-clear': selectedFilter === 'all' }"
            @click="selectFilter('all')"
          >
            {{ $t('PAYMENTS.ALL') }}
          </div>
          <div
            class="bg-white dark:bg-slate-900 cursor-pointer filter-color"
            :class="{ 'import-clear': selectedFilter === 'today' }"
            @click="selectFilter('today')"
          >
            {{ $t('PAYMENTS.TODAY') }}
          </div>
          <div
            class="filter-color cursor-pointer bg-white dark:bg-slate-900"
            :class="{ 'import-clear': selectedFilter === 'lastWeek' }"
            @click="selectFilter('lastWeek')"
          >
            {{ $t('PAYMENTS.LAST_WEEK') }}
          </div>
          <div
            class="filter-color cursor-pointer bg-white dark:bg-slate-900"
            :class="{ 'import-clear': selectedFilter === 'lastMonth' }"
            @click="selectFilter('lastMonth')"
          >
            {{ $t('PAYMENTS.LAST_MONTH') }}
          </div>
          <div
            class="filter-color cursor-pointer bg-white dark:bg-slate-900"
            :class="{ 'import-clear': selectedFilter === 'custom' }"
            @click="toggleCustomFilter"
          >
            {{ $t('PAYMENTS.CUSTOM') }}
          </div>
        </div>

        <!-- Date Picker only shows if 'custom' is selected -->
        <div v-if="selectedFilter === 'custom'" class="mt-4 position-absolute">
          <div class="date-picker">
            <date-picker
              :range="true"
              :confirm="true"
              :clearable="false"
              :editable="false"
              :confirm-text="confirmText"
              :placeholder="placeholder"
              :value="value"
              @change="handleChange"
              v-model="selectedDate"
            />
          </div>
        </div>
      </div>
    </div>
    <div v-if="paymentsData.payments.length > 0">
      <sample-tickets :payments="paymentsData" @download_csv="downloadCSV" :selectedFilters="selectedFilter" :selectedDateCustom="selectedDate"/>
    </div>
    <div v-else-if="!uiFlags.isFetching && paymentsData.payments.length == 0" class="h-[60%] grid place-content-center font-bold text-[33px]">
      {{ $t('PAYMENTS.NO_PAYMENT_HISTORY') }}
    </div>
    <div v-if="uiFlags.isFetching" class="loadingScreeen">
      <loading-state v-if="uiFlags.isFetching" :message="emptyStateMessage" />
    </div>
  </div>
</template>
<script>
import { mapGetters } from 'vuex';
import DatePicker from 'vue2-datepicker';
import LoadingState from 'dashboard/components/widgets/LoadingState';
import SampleTickets from './SampleTickets.vue';

export default {
  components: {
    DatePicker,
    SampleTickets,
    LoadingState,
  },
  data() {
    return {
      assignedAgent: null,
      selectedDate: null,
      confirmText: 'Select Date Range',
      placeholder: 'Select date range',
      value: [],
      selectedFilter: 'all',
      emptyStateMessage: 'Loading Payments',
      csv: false,
    };
  },
  computed: {
    ...mapGetters({
      agentList: 'agents/getAgents',
      uiFlags: 'payments/getUIFlags',
    }),
    paymentsData() {
      const data = this.$store.getters['payments/getPayments'];
      this.$emit('payments-fetched', data);
      return data;
    },
  },
  mounted() {
    this.$store.dispatch('agents/get');
    this.filterData(); // initial fetch
  },
  methods: {
    onClickAssignAgent(selectedAgent) {
      this.filterData();
    },

    handleChange(value) {
      this.selectedDate = value;
      this.filterData();
    },

    selectFilter(filterKey) {
      this.selectedFilter = filterKey;

      const today = new Date();

      if (filterKey === 'all') {
        this.selectedDate = null;
      } else if (filterKey === 'today') {
        this.selectedDate = [today, today];
      } else if (filterKey === 'lastWeek') {
        const sevenDaysAgo = new Date();
        sevenDaysAgo.setDate(today.getDate() - 7);
        this.selectedDate = [sevenDaysAgo, today];
      } else if (filterKey === 'lastMonth') {
        const oneMonthAgo = new Date();
        oneMonthAgo.setMonth(today.getMonth() - 1);
        this.selectedDate = [oneMonthAgo, today];
      }

      this.filterData();
      this.graphData();
    },

    toggleCustomFilter() {
      // this.selectedFilter = this.selectedFilter === 'custom' ? null : 'custom';
       if (this.selectedFilter !== 'custom') {
          this.selectedFilter = 'custom';
          this.selectedDate = null; // ⬅️ Clears previous selection
        } else {
          this.selectedFilter = null;
        }
    },

    async filterData(csv = false) {
      const [startDateRaw, endDateRaw] = this.selectedDate || [];
      const agentId = this.assignedAgent?.id || null;

      const formatDate = dateStr => {
        const date = new Date(dateStr);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
      };

      const payload = {
        start_date: startDateRaw ? formatDate(startDateRaw) : null,
        end_date: endDateRaw ? formatDate(endDateRaw) : null,
        agent_ids: agentId ? [agentId] : [],
        csv,
      };

      await this.$store.dispatch('payments/PaymentFilter', payload);
    },

    async graphData() {
      const [startDateRaw, endDateRaw] = this.selectedDate || [];

      const formatDate = dateStr => {
        const date = new Date(dateStr);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
      };

      const payload = {
        start_date: startDateRaw ? formatDate(startDateRaw) : null,
        end_date: endDateRaw ? formatDate(endDateRaw) : null,
      };

      await this.$store.dispatch('payments/GraphView', payload);
    },

    downloadCSV(value) {
      if (value === true) {
        this.filterData(true);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.payment-dashboard-ui {
  padding: 25px;
}
.agent-dropdown {
  width: 11%;
}
.filter-color {
  padding: 5px;
  // background-color: #fff;
  border-radius: 5px;
  border: 1px solid lightgrey;
}
.import-clear {
  background-color: #40518a !important;
  color: #fff;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.2);
}
.loadingScreeen{
  position: absolute;
  z-index: 30;
  background-color: #443f3f6e;
  inset: 0;
  display: grid;
  place-items: center;
  font-size: 30px;
  color: white;
}
</style>